/**
 * RESPONSIBILITY: the @StringList column of a @List row.
 *
 * Plain strings a row keeps its own set of — not the vocabulary a @Choice({in}) points
 * at, which lives on the section above. Without it the cell lookup fell through to text,
 * so an array rendered as `a,b,c` in a box and was saved back as the string "a,b,c".
 * That is the failure worth naming: the fallback did not merely look wrong, it changed
 * the type of what was stored.
 */
import { useState } from 'react';
import type { Cell } from './cell-types';

const StringListCell: Cell['render'] = ({ field, value, onChange, id }) => {
  const [draft, setDraft] = useState('');
  const values: string[] = Array.isArray(value) ? (value as string[]) : [];
  const full = field.maxItems !== undefined && values.length >= field.maxItems;

  // Duplicates are ignored rather than refused, exactly as the full-size control does:
  // this is a set, and adding one twice is a slip, not something to interrupt for.
  function add() {
    const v = draft.trim();
    if (!v || full || values.includes(v)) { setDraft(''); return; }
    onChange([...values, v]);
    setDraft('');
  }

  return (
    <div className="ctl-cell-strings">
      <div className="ctl-chips">
        {values.map((v) => (
          <span key={v} className="ctl-chip">
            {v}
            <button
              type="button" className="ctl-chip-x"
              onClick={() => onChange(values.filter((x) => x !== v))}
              title={`Remove ${v}`} aria-label={`Remove ${v}`}
            >✕</button>
          </span>
        ))}
      </div>
      <input
        id={id} type="text" className="ctl-input" value={draft} disabled={full}
        placeholder={full ? `Full (${field.maxItems})` : 'Type and press Enter'}
        onChange={(e) => setDraft(e.target.value)}
        // Enter would submit the dialog's form and save the whole section instead.
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        onBlur={add}
      />
    </div>
  );
};

export const stringListCell: Cell = { render: StringListCell, basis: () => '100%' };
